import { defineStore } from 'pinia';
import storeId from './storeId';
import type { UserData, UserState } from '../types/user-type';
import { userApis } from '@/axiosModel';
import type { ApiResponse } from '@/axiosModel/Model.class';
import { isFreeUser as checkIsFreeUser, isPremiumUser as checkIsPremiumUser } from '@/api/user';
import { fetchUserMeta, setUserMeta } from '@/api/common';
import type { UserMeta } from '@/types';
import { isAuthDisabled } from '@/utils/login';

interface UserApiData extends Partial<Record<keyof UserData, unknown>> {
  aipicturebook_vip_type?: unknown;
}

function normalizeUserData(data: UserApiData | undefined, currentUserData: UserData): UserData {
  const userData = data ?? {};

  const vipType = toNumberValue(userData.aipicturebook_vip_type ?? userData.uType, 0);

  return {
    ...currentUserData,
    ...userData,
    userId: toNumberValue(userData.userId, 0),
    uName: toStringValue(userData.uName),
    // 本地仍使用 uType 承接会员等级，来源优先为后端 aipicturebook_vip_type。
    uType: vipType,
    aipicturebook_vip_type: vipType,
    phone: toStringValue(userData.phone),
    userImg: normalizeUserImage(userData.userImg),
    newTime: toNumberValue(userData.newTime, 0),
    newTimeDate: toOptionalStringValue(userData.newTimeDate),
    expireTime: toNumberValue(userData.expireTime, 0),
    expireDate: toOptionalStringValue(userData.expireDate),
    mobilePhone: toStringValue(userData.mobilePhone),
    uEmail: toStringValue(userData.uEmail),
    areaCode: toStringValue(userData.areaCode),
    verifyPhone:
      userData.verifyPhone === undefined ? undefined : toNumberValue(userData.verifyPhone, 0),
    hasPassword: userData.hasPassword === undefined ? 1 : toNumberValue(userData.hasPassword, 1),
    isUpdateName: toNumberValue(userData.isUpdateName, currentUserData.isUpdateName),
    company: toStringValue(userData.company),
    website: toStringValue(userData.website),
    about: toStringValue(userData.about),
    address: toStringValue(userData.address),
    fullName: toStringValue(userData.fullName),
    personalPhone: toStringValue(userData.personalPhone),
    qqCode: toStringValue(userData.qqCode),
    postalcode: toStringValue(userData.postalcode),
    caption: toStringValue(userData.caption),
    invoiceTitle: toStringValue(userData.invoiceTitle),
    city: toStringValue(userData.city),
    state: toStringValue(userData.state),
    countryId: toOptionalNumberValue(userData.countryId),
    auditStatus: toNumberValue(userData.auditStatus, 0),
    account: toStringValue(userData.account)
  };
}

function normalizeUserImage(value: unknown) {
  const userImg = toStringValue(value);
  return userImg ? `${userImg}?${Date.now()}&x-oss-process=image/resize,l_100` : '';
}

function toStringValue(value: unknown) {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

function toOptionalStringValue(value: unknown) {
  const stringValue = toStringValue(value);
  return stringValue || undefined;
}

function toNumberValue(value: unknown, fallback: number) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function toOptionalNumberValue(value: unknown) {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : undefined;
}

export const useUserStore = defineStore(storeId.USER, {
  state: (): UserState => ({
    isLogin: false,
    userData: {
      userId: 0,
      uName: '',
      uType: 0,
      userImg: '',
      phone: '',
      newTime: 0,
      expireTime: 0,
      mobilePhone: '',
      uEmail: '',
      areaCode: '',
      verifyPhone: undefined,
      hasPassword: 1,
      isUpdateName: 1,
      company: '',
      website: '',
      about: '',
      address: '',
      fullName: '',
      personalPhone: '',
      qqCode: '',
      postalcode: '',
      caption: '',
      invoiceTitle: '',
      city: '',
      state: '',
      countryId: undefined,
      auditStatus: 0,
      account: ''
    },
    showBindPhone: false,
    levelNameMap: {
      0: '免费用户',
      1: '基础会员',
      2: '高级会员'
    },
    levelName: '',
    userMeta: {}
  }),
  getters: {
    /** 是否为免费用户（AI 绘本会员等级 uType === 0） */
    isFreeUser: (state) => checkIsFreeUser(state.userData),
    /** 是否为高级会员（AI 绘本会员等级 uType === 2） */
    isPremiumUser: (state) => checkIsPremiumUser(state.userData),
    /** 是否已绑定手机号（仅以 mobilePhone 为准） */
    hasBoundPhone: (state) => Boolean(state.userData.mobilePhone.trim())
  },
  actions: {
    async getUser() {
      try {
        const response = (await userApis.getUser().req) as ApiResponse<UserApiData>;
        if (response.code === 'OK') {
          this.userData = normalizeUserData(response.data, this.userData);
          this.levelName = this.levelNameMap[Number(this.userData.uType)] || '';
          this.userMeta = await fetchUserMeta();
          this.isLogin = true;
          return true;
        }

        // DEV / 免登录：仍走接口；未登录会话不弹登录，保持可用
        if (isAuthDisabled()) {
          this.isLogin = true;
          this.levelName = this.levelNameMap[Number(this.userData.uType)] || '';
          return true;
        }

        this.isLogin = false;
        this.levelName = '';
        return false;
      } catch (error) {
        if (isAuthDisabled()) {
          this.isLogin = true;
          this.levelName = this.levelNameMap[Number(this.userData.uType)] || '';
          console.warn(error, 'getUser: auth disabled, keep local login state');
          return true;
        }

        this.isLogin = false;
        this.levelName = '';
        console.error(error, 'getUser error');
        return false;
      }
    },
    /** 更新 userMeta 并同步到后端 */
    async setMeta(data: UserMeta) {
      await setUserMeta(data);
      this.userMeta = { ...this.userMeta, ...data };
    },
    /** 从后端重新拉取 userMeta */
    async refreshMeta() {
      this.userMeta = await fetchUserMeta();
    },
    /** 清除本地登录状态 */
    async signOut() {
      await userApis.signOut().req;
      this.isLogin = false;
      this.levelName = '';
    }
  }
});
